import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, Instagram, Youtube, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGOS = [
  "0 – 1.000€",
  "1.000€ – 5.000€",
  "5.000€ – 10.000€",
  "10.000€ – 50.000€",
  "50.000€+",
];

export default function LeadForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    phone: "",
    rango_facturacion: "",
    instagram: "",
    youtube: "",
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = "Introduce tu email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Email no válido";

    if (!form.phone.trim()) e.phone = "Introduce tu teléfono";
    else {
      const digits = form.phone.replace(/\D/g, "");
      if (digits.length < 6) e.phone = "Teléfono no válido";
    }

    if (!form.rango_facturacion) e.rango_facturacion = "Selecciona un rango";
    if (!form.instagram.trim()) e.instagram = "Introduce tu cuenta de Instagram";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        email: form.email.trim(),
        phone: form.phone.trim(),
        rango_facturacion: form.rango_facturacion,
        instagram: form.instagram.trim(),
        youtube: form.youtube.trim() || null,
      };
      const { data } = await axios.post(`${API}/leads`, payload, {
        timeout: 25000,
      });
      if (data && data.success) {
        navigate("/gracias");
      } else {
        setServerError("No se pudo enviar. Vuelve a intentarlo.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Error de conexión. Inténtalo de nuevo.";
      setServerError(typeof msg === "string" ? msg : "Error inesperado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section section-dark" id="formulario">
      <div className="container" style={{ maxWidth: 720 }}>
        <div
          className="eyebrow-yellow"
          style={{ textAlign: "center", marginBottom: "0.8rem" }}
        >
          Regístrate a la masterclass
        </div>
        <h2
          className="h2-section"
          style={{ textAlign: "center", marginBottom: "1rem" }}
        >
          Te espero <span className="serif-italic hl-purple">en directo</span>.
        </h2>
        <p
          style={{
            textAlign: "center",
            color: "var(--text-dim)",
            margin: "0 auto 2rem",
            maxWidth: 580,
          }}
        >
          Déjame tus datos y te enviaré el acceso a la clase por email y
          WhatsApp. Las plazas son limitadas.
        </p>

        <form
          onSubmit={onSubmit}
          className="form-card"
          noValidate
          data-testid="lead-form"
        >
          {serverError && (
            <div className="error-banner" data-testid="server-error">
              {serverError}
            </div>
          )}

          <div className="field">
            <label htmlFor="email">
              Email <span className="req">*</span>
            </label>
            <div className="input-with-prefix">
              <Mail size={16} className="prefix" />
              <input
                id="email"
                type="email"
                className="input"
                placeholder="tucorreo@ejemplo.com"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                autoComplete="email"
                data-testid="input-email"
              />
            </div>
            {errors.email && (
              <div className="field-error" data-testid="err-email">
                {errors.email}
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="phone">
              Teléfono <span className="req">*</span>
            </label>
            <div className="input-with-prefix">
              <Phone size={16} className="prefix" />
              <input
                id="phone"
                type="tel"
                className="input"
                placeholder="+34 600 000 000"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                autoComplete="tel"
                data-testid="input-phone"
              />
            </div>
            {errors.phone && (
              <div className="field-error" data-testid="err-phone">
                {errors.phone}
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="rango">
              Rango de facturación <span className="req">*</span>
            </label>
            <select
              id="rango"
              className="select"
              value={form.rango_facturacion}
              onChange={(e) => setField("rango_facturacion", e.target.value)}
              data-testid="select-rango"
            >
              <option value="">Selecciona un rango...</option>
              {RANGOS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {errors.rango_facturacion && (
              <div className="field-error" data-testid="err-rango">
                {errors.rango_facturacion}
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="instagram">
              Instagram <span className="req">*</span>
            </label>
            <div className="input-with-prefix">
              <Instagram size={16} className="prefix" />
              <input
                id="instagram"
                type="text"
                className="input"
                placeholder="@tucuenta"
                value={form.instagram}
                onChange={(e) => setField("instagram", e.target.value)}
                data-testid="input-instagram"
              />
            </div>
            {errors.instagram && (
              <div className="field-error" data-testid="err-instagram">
                {errors.instagram}
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="youtube">
              YouTube{" "}
              <span style={{ color: "#666", fontWeight: 500 }}>(opcional)</span>
            </label>
            <div className="input-with-prefix">
              <Youtube size={16} className="prefix" />
              <input
                id="youtube"
                type="text"
                className="input"
                placeholder="@tucanal o URL"
                value={form.youtube}
                onChange={(e) => setField("youtube", e.target.value)}
                data-testid="input-youtube"
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn-neon"
            disabled={loading}
            style={{ width: "100%", marginTop: "0.8rem" }}
            data-testid="submit-btn"
          >
            {loading ? (
              <>
                <span className="spinner" /> Enviando...
              </>
            ) : (
              <>
                Reservar mi plaza <ArrowRight size={18} />
              </>
            )}
          </button>

          <p
            style={{
              textAlign: "center",
              color: "#666",
              fontSize: "0.78rem",
              marginTop: "1rem",
            }}
          >
            Al enviar aceptas nuestra política de privacidad. Tus datos están
            seguros.
          </p>
        </form>
      </div>
    </section>
  );
}
